export type PdfStatementPage = {
  page: number;
  method: "text" | "ocr";
  text: string;
  image: string;
  confidence?: number;
};

export function validatePdfInput(bytes: Uint8Array, size: number): void {
  if (size > 5 * 1024 * 1024) throw new Error("The PDF exceeds the 5 MiB limit.");
  if (new TextDecoder().decode(bytes.subarray(0, 5)) !== "%PDF-") throw new Error("This is not a PDF file.");
}

export function reviewedPdfCsv(csv: string, reviewed: boolean): string {
  if (!reviewed) throw new Error("Please review every page, date, amount and debit/credit sign first.");
  const normalized = csv.trim().replace(/\r\n/g, "\n");
  if (!normalized.startsWith("date,description,amount\n")) throw new Error("Use the header date,description,amount and add at least one transaction row.");
  if (normalized.length > 100_000) throw new Error("The reviewed rows exceed the extraction limit.");
  return normalized;
}

export function boundedPdfScale(width: number, height: number): number {
  if (!Number.isFinite(width * height) || width <= 0 || height <= 0) throw new Error("Invalid PDF page dimensions.");
  // Area alone does not bound an extremely tall/thin malicious page.
  return Math.min(2, 4096 / width, 4096 / height, Math.sqrt(2_000_000 / (width * height)));
}

/** Extracts all pages or rejects. Results are local review material, never transactions. */
export async function extractPdfStatement(file: File, signal: AbortSignal, progress: (text: string) => void, forceOcr = false): Promise<PdfStatementPage[]> {
  if (file.size > 5 * 1024 * 1024) throw new Error("The PDF exceeds the 5 MiB limit.");
  const bytes = new Uint8Array(await file.arrayBuffer());
  validatePdfInput(bytes, file.size);
  signal.throwIfAborted();
  const pdfjs = await import("pdfjs-dist");
  const { default: workerUrl } = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
  signal.throwIfAborted();
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
  const base = new URL(import.meta.env.BASE_URL, window.location.origin);
  const task = pdfjs.getDocument({
    data: bytes, enableXfa: false, stopAtErrors: true,
    maxImageSize: 4_000_000, canvasMaxAreaInBytes: 16_000_000,
    cMapUrl: new URL("pdf/cmaps/", base).href, cMapPacked: true,
    standardFontDataUrl: new URL("pdf/standard_fonts/", base).href,
    wasmUrl: new URL("pdf/wasm/", base).href,
  });
  let ocr: import("tesseract.js").Worker | undefined;
  let renderTask: import("pdfjs-dist").RenderTask | undefined;
  let stopped = false;
  let interrupt!: (reason: Error) => void;
  const interrupted = new Promise<never>((_, reject) => { interrupt = reject; });
  const wait = <T,>(promise: Promise<T>) => Promise.race([promise, interrupted]);
  const stop = () => {
    if (stopped) return;
    stopped = true;
    interrupt(new Error(signal.aborted ? "PDF extraction cancelled." : "PDF extraction timed out. No partial document was imported."));
    renderTask?.cancel();
    void task.destroy().catch(() => undefined);
    void ocr?.terminate().catch(() => undefined);
  };
  const timer = window.setTimeout(stop, 90_000);
  signal.addEventListener("abort", stop, { once: true });
  if (signal.aborted) stop();
  const check = () => {
    signal.throwIfAborted();
    if (stopped) throw new Error("PDF extraction timed out. No partial document was imported.");
  };
  try {
    const document = await wait(task.promise);
    check();
    if (document.numPages > 10) throw new Error("PDF review supports at most 10 pages. Split the statement; no pages were imported.");
    const pages: PdfStatementPage[] = [];
    let chars = 0;
    for (let number = 1; number <= document.numPages; number++) {
      check();
      progress(`Reading page ${number} of ${document.numPages}…`);
      const page = await wait(document.getPage(number));
      const text = await wait(page.getTextContent());
      const extracted = text.items.map(item => "str" in item ? `${item.str}${item.hasEOL ? "\n" : " "}` : "").join("").trim();
      const natural = page.getViewport({ scale: 1 });
      const scale = boundedPdfScale(natural.width, natural.height);
      const viewport = page.getViewport({ scale });
      const canvas = window.document.createElement("canvas");
      canvas.width = Math.max(1, Math.floor(viewport.width));
      canvas.height = Math.max(1, Math.floor(viewport.height));
      try {
        renderTask = page.render({ canvas, viewport });
        await wait(renderTask.promise);
        check();
        let content = extracted;
        let confidence: number | undefined;
        // Sparse text may be only a page header over a scanned statement.
        const method = !forceOcr && extracted.replace(/\s/g, "").length >= 40 ? "text" : "ocr";
        if (method === "ocr") {
          progress(`Recognizing page ${number} of ${document.numPages} on this device…`);
          if (!ocr) {
            const { createWorker } = await import("tesseract.js");
            ocr = await wait(createWorker("eng", 1, {
              workerPath: new URL("ocr/worker.min.js", base).href,
              corePath: new URL("ocr/core/", base).href,
              langPath: new URL("ocr/", base).href,
              workerBlobURL: false, cacheMethod: "none",
            }).then(worker => { if (stopped) void worker.terminate(); return worker; }));
            check();
          }
          if (!ocr) throw new Error("OCR worker did not initialize. Nothing was imported.");
          const result = await wait(ocr.recognize(canvas));
          check();
          content = result.data.text.trim();
          confidence = result.data.confidence;
        }
        chars += content.length;
        if (chars > 100_000) throw new Error("The PDF exceeds the 100,000-character extraction limit.");
        pages.push({ page: number, method, text: content, confidence, image: canvas.toDataURL("image/png") });
      } finally {
        canvas.width = canvas.height = 0;
        page.cleanup();
      }
    }
    return pages;
  } catch (error) {
    check();
    if (error instanceof Error && error.name === "PasswordException") throw new Error("Remove the PDF password on your device before importing. No password is uploaded.");
    throw error;
  } finally {
    window.clearTimeout(timer);
    signal.removeEventListener("abort", stop);
    await task.destroy();
    await ocr?.terminate();
  }
}
